import React, { useState } from 'react';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import styled from 'styled-components';
import Colors from '@components/shared/Colors';
import PricingModal from '@components/shared/PricingModal';
import { P } from '@components/shared/Typography';
import IconHowTo from '@images/sampler/icon-how-to.svg';
import IconPriceBreakDown from '@images/sampler/icon-price-breakdown.svg';
import IconSafety from '@images/sampler/icon-safety.svg';
import useNavigate from '@utils/useNavigate';

interface Props {
  backgroundColor: string;
}

const ModalTriggersRow: React.FC<Props> = ({ backgroundColor }) => {
  const { t } = useTranslation();
  const { navigateWrapper } = useNavigate();
  const [isOpen, setIsOpen] = useState(false);

  const triggers = [
    {
      text: t('tampon-modal-row.safety'),
      icon: IconSafety,
      onClick: () => navigateWrapper('https://www.yourdaye.com/vitals/cbd/are-cbd-tampons-safe')
    },
    {
      text: t('tampon-modal-row.how-cbd-works'),
      icon: IconHowTo,
      onClick: () => navigateWrapper('https://www.yourdaye.com/vitals/cbd/do-cbd-tampons-work')
    },
    {
      text: t('tampon-modal-row.supply-chain'),
      icon: IconPriceBreakDown,
      onClick: () => true
    }
  ];

  const onTriggerClick = (func: Function) => {
    if (typeof func() === 'boolean') {
      setIsOpen(func());
    }
    return func();
  };

  return (
    <Container>
      {triggers.map((trigger, index) => (
        <div key={index}>
          <TriggerWrap $backgroundColor={backgroundColor} onClick={() => onTriggerClick(trigger.onClick)} key={index}>
            <TextWrap>{trigger.text}</TextWrap>
            <IconWrap>
              <trigger.icon />
            </IconWrap>
          </TriggerWrap>
          {index === 2 && (
            <PricingModal
              isOpen={isOpen}
              closeModal={() => {
                setIsOpen(false);
                document.body.removeAttribute('style');
              }}
            />
          )}
        </div>
      ))}
    </Container>
  );
};

export default ModalTriggersRow;

const Container = styled.div`
  color: ${Colors.forestGreen};
  display: flex;
  justify-content: space-around;
  padding: 0 60px;
  max-width: 1440px;
  margin: auto;

  @media (max-width: 1220px) {
    align-self: center;
    flex-direction: column;
    align-items: center;
    padding: 0 30px 40px 30px;
  }
`;

interface ITriggerWrap {
  $backgroundColor: string;
}

const TriggerWrap = styled.button<ITriggerWrap>`
  background: ${(props) => (props.$backgroundColor ? props.$backgroundColor : '')}
  border: none;
  outline: none;
  display: flex;
  padding: 30px 35px;
  max-height: 115px;
  max-width: 413px;
  margin: 0 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-grow: 1;

  &:hover{
    opacity: 0.6;
    cursor: pointer;
  }
  @media (max-width: 1220px) {
    margin: 0;
    &:nth-child(-n+2) {
      margin-bottom: 20px;
    }
    column-gap: 20px;
    padding: 20px 30px 20px 20px;
    justify-content: space-between;
    width: 100%;
  }
`;

const TextWrap = styled(P)`
  font-family: BrownStd;
  font-size: 18px;
  font-weight: 700;
  line-height: 27px;
  text-align: left;
  max-width: 185px;

  @media (max-width: 1024px) {
    font-size: 16px;
    line-height: 22px;
  }
`;

const IconWrap = styled.div`
  display: flex;
  justify-content: center;
  align-self: center;
  & > svg {
    max-width: 88px;
    max-height: 50px;
    @media (max-width: 1024px) {
      max-width: 55px;
      max-height: 35px;
    }
  }
`;
